import { TAGS_SELECT } from 'constants/utils';
import { Dispatch, SetStateAction } from 'react';
import { ReportService } from 'services/Reports/ReportService';
import { AgentResponse, TagResponse, TicketsResponse } from 'typings';
import { TableDataModel } from 'typings/components/TableDataModel';
import TicketTabsEnum from 'typings/enums/TicketTabsEnum';
import { nameof, paginate, retryBatchRequest } from 'utils';

export const fetchTags = async (
    reportApi: ReportService,
    bots: string[],
    pageSize: number,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    setTags: Dispatch<SetStateAction<TableDataModel<TagResponse['tags']>>>,
    abortController?: AbortController,
) => {
    try {
        const defaultRequestPageSize = 1000000;
        const tags = [] as any[];
        let count = 0;

        setIsLoading(true);

        await retryBatchRequest<TagResponse>(
            reportApi,
            nameof<ReportService>(TAGS_SELECT),
            (items: any[]) => [items, 1, defaultRequestPageSize],
            (result, amount) => {
                if (abortController?.signal.aborted) {
                    return;
                }

                count += amount;

                if (bots.length == 1) {
                    tags.push(...result.tags);
                } else {
                    result.tags.forEach((t: { name: any; closedTickets: any }) => {
                        const tag = tags.find((i: { name: any }) => i.name === t.name);
                        if (tag) {
                            tag.closedTickets.amount += t.closedTickets.amount;
                        } else {
                            tags.push({ ...t });
                        }
                    });
                }

                tags.sort((a, b) => a.name.localeCompare(b.name));

                if (!abortController?.signal.aborted) {
                    setTags({
                        data: tags,
                        filteredData: paginate(tags, pageSize, 1),
                        searchText: '',
                        activeTab: TicketTabsEnum.Tags,
                        currentPage: 1,
                        percentageLoaded: Math.floor((count / bots.length) * 100),
                    });
                }
            },
            bots,
        );
    } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching tags:', error);
        }
        return;
    } finally {
        if (!abortController?.signal.aborted) {
            setIsLoading(false);
        }
    }
};

export const fetchAgents = async (
    reportApi: ReportService,
    bots: string[],
    pageSize: number,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    setAgents: Dispatch<SetStateAction<TableDataModel<AgentResponse['attendants']>>>,
    abortController?: AbortController,
) => {
    try {
        const defaultRequestPageSize = 1000000;
        const agents = [] as any[];
        let count = 0;

        setIsLoading(true);

        await retryBatchRequest<AgentResponse>(
            reportApi,
            nameof<ReportService>('getAgents'),
            (items: any[]) => [items, 1, defaultRequestPageSize],
            (result, amount) => {
                if (abortController?.signal.aborted) {
                    return;
                }

                count += amount;
                if (bots.length == 1) {
                    agents.push(...result.attendants);
                } else {
                    result.attendants.forEach((a: { name: any; status: any; openedTickets: any }) => {
                        const agent = agents.find((i: { name: any }) => i.name === a.name);
                        if (agent) {
                            if (agent.status !== a.status) {
                                agent.status = undefined;
                            }
                            agent.openedTickets += a.openedTickets;
                        } else {
                            agents.push({ ...a });
                        }
                    });
                }
                agents.sort((a, b) => a.name.localeCompare(b.name));

                if (!abortController?.signal.aborted) {
                    setAgents({
                        data: agents,
                        filteredData: paginate(agents, pageSize, 1),
                        searchText: '',
                        activeTab: TicketTabsEnum.Agents,
                        currentPage: 1,
                        percentageLoaded: Math.floor((count / bots.length) * 100),
                    });
                }
            },
            bots,
        );
    } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching agents:', error);
        }
        return;
    } finally {
        if (!abortController?.signal.aborted) {
            setIsLoading(false);
        }
    }
};

export const fetchTicketsInQueue = async (
    reportApi: ReportService,
    bots: string[],
    pageSize: number,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    setTicketsInQueue: Dispatch<SetStateAction<TableDataModel<TicketsResponse['tickets']>>>,
    abortController?: AbortController,
) => {
    try {
        const defaultRequestPageSize = 1000000;
        const tickets = [] as TicketsResponse['tickets'];
        let count = 0;

        setIsLoading(true);

        await retryBatchRequest<TicketsResponse>(
            reportApi,
            nameof<ReportService>('getTicketInQueue'),
            (items: TicketsResponse[]) => [items, 1, defaultRequestPageSize],
            (result, amount) => {
                if (abortController?.signal.aborted) {
                    return;
                }

                count += amount;
                tickets.push(...result.tickets);
                tickets.sort((a, b) => a.number - b.number);

                if (!abortController?.signal.aborted) {
                    setTicketsInQueue({
                        data: tickets,
                        filteredData: paginate(tickets, pageSize, 1),
                        searchText: '',
                        activeTab: TicketTabsEnum.TicketsInQueue,
                        currentPage: 1,
                        percentageLoaded: Math.floor((count / bots.length) * 100),
                    });
                }
            },
            bots,
        );
    } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching tickets in queue:', error);
        }
        return;
    } finally {
        if (!abortController?.signal.aborted) {
            setIsLoading(false);
        }
    }
};

export const fetchTicketsInAttendance = async (
    reportApi: ReportService,
    bots: string[],
    pageSize: number,
    setIsLoading: Dispatch<SetStateAction<boolean>>,
    setTicketsInAttendance: Dispatch<SetStateAction<TableDataModel<TicketsResponse['tickets']>>>,
    abortController?: AbortController,
) => {
    try {
        const defaultRequestPageSize = 1000000;
        const tickets = [] as any[];
        let count = 0;

        setIsLoading(true);

        await retryBatchRequest<TicketsResponse>(
            reportApi,
            nameof<ReportService>('getTicketsInAttendance'),
            (items: any[]) => [items, 1, defaultRequestPageSize],
            (result, amount) => {
                if (abortController?.signal.aborted) {
                    return;
                }

                count += amount;
                tickets.push(...result.tickets);
                tickets.sort((a, b) => a.number - b.number);

                if (!abortController?.signal.aborted) {
                    setTicketsInAttendance({
                        data: tickets,
                        filteredData: paginate(tickets, pageSize, 1),
                        searchText: '',
                        activeTab: TicketTabsEnum.TicketsInAttendance,
                        currentPage: 1,
                        percentageLoaded: Math.floor((count / bots.length) * 100),
                    });
                }
            },
            bots,
        );
    } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching tickets in attendance:', error);
        }
        return;
    } finally {
        if (!abortController?.signal.aborted) {
            setIsLoading(false);
        }
    }
};
